import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { RaffleGate } from "@/components/RaffleGate";
import { useI18n, ASSETS, SECTION_IMAGES } from "@/lib/i18n";
import { usePageSEO, SEO } from "@/lib/seo";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import SafeImg from "@/components/SafeImg";
import { RichText } from "@/components/RichText";
import { useCmsPage, pickTx, paragraphs } from "@/lib/pageCms";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } };

export function Mission() {
  const cms = useCmsPage("mission");
  usePageSEO(SEO.mission.title, SEO.mission.description, { cmsSeo: cms.seo });
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;

  // Defensive fallback — never let a missing image URL crash the page.
  const missionHero = (SECTION_IMAGES && SECTION_IMAGES.mission) || {
    image: ASSETS?.planningMeeting || "",
    alt: { da: "", en: "", es: "" },
  };
  const heroAlt = (missionHero.alt && (missionHero.alt[lang] || missionHero.alt.en)) || "";
  const fallbackImg = ASSETS?.paella || "";

  // CMS-first hero and chapters. Falls back to the legacy hardcoded copy
  // whenever the CMS is not live yet OR a specific section is missing.
  const cmsHero = cms.enabled ? cms.sections.find(s => s.slug === "hero" && s.template === "hero") : null;
  const cmsChapters = cms.enabled ? cms.sections.filter(s => s.template === "chapter") : [];

  const legacyChapters = [
    {
      kicker: t("KAPITEL 01", "CHAPTER 01", "CAPÍTULO 01"),
      title: t("Alt begyndte med én beslutning.", "Everything started with one decision.", "Todo empezó con una decisión."),
      body: [
        t(
          "I efteråret 2024 ramte DANA-oversvømmelserne tusindvis af familier i Valencia.",
          "In autumn 2024 the devastating floods caused by DANA affected thousands of families in Valencia.",
          "En otoño de 2024, las devastadoras inundaciones provocadas por la DANA afectaron a miles de familias en Valencia."
        ),
        t(
          "Selvom vi boede over 2.000 kilometer væk i København, troede vi ikke på afstand som undskyldning.",
          "Even though we lived more than 2,000 kilometres away in Copenhagen, we believed distance could never be an excuse.",
          "Aunque vivíamos a más de 2.000 kilómetros en Copenhague, creíamos que la distancia nunca podía ser una excusa."
        ),
        t(
          "En gruppe frivillige besluttede at handle. Den beslutning blev til DANAmark Solidaritet.",
          "A group of volunteers decided to act. That decision became DANAmark Solidaritet.",
          "Un grupo de voluntarios decidió actuar. Esa decisión se convirtió en DANAmark Solidaritet."
        ),
      ],
      image: ASSETS?.planningMeeting,
      caption: t("Alt begyndte her.", "Everything started here.", "Todo empezó aquí."),
    },
    {
      kicker: t("KAPITEL 02", "CHAPTER 02", "CAPÍTULO 02"),
      title: t("Mere end en indsamling.", "More than fundraising.", "Más que una recaudación."),
      body: [
        t(
          "Musikere, kunstnere, virksomheder, frivillige og familier fandt hinanden omkring én sag. Fodboldspillere donerede signerede trøjer. Vi holdt fællesspisning, kulturaftener, koncerter og velgørenhedsaktioner — hver på sin måde, alle drevet af det samme.",
          "Musicians, artists, businesses, volunteers and families found each other around one cause. Football players donated signed shirts. We held community dinners, cultural evenings, concerts and fundraising activities — each in its own way, all driven by the same thing.",
          "Músicos, artistas, empresas, voluntarios y familias se encontraron en torno a una misma causa. Futbolistas donaron camisetas firmadas. Organizamos cenas comunitarias, veladas culturales, conciertos y actividades benéficas — cada una a su manera, todas movidas por lo mismo."
        ),
      ],
      image: ASSETS?.jerseys,
      caption: t("Solidaritet begynder med mennesker.", "Solidarity begins with people.", "La solidaridad empieza por las personas."),
    },
    {
      kicker: t("KAPITEL 03", "CHAPTER 03", "CAPÍTULO 03"),
      title: t("Sammen samlede vi over 200.000 DKK.", "Together we raised more than 200,000 DKK.", "Juntos recaudamos más de 200.000 DKK."),
      body: [
        t(
          "Alle midler blev doneret direkte til Fundación Isabel Enrique Díaz og World Central Kitchen — to betroede humanitære partnere, der leverede støtten direkte til familierne, der var ramt af oversvømmelserne i Valencia.",
          "All funds were donated directly to Fundación Isabel Enrique Díaz and World Central Kitchen — two trusted humanitarian partners who delivered the support directly to families affected by the floods in Valencia.",
          "Todos los fondos se donaron directamente a la Fundación Isabel Enrique Díaz y a World Central Kitchen — dos socios humanitarios de confianza que entregaron el apoyo directamente a las familias afectadas por las inundaciones en Valencia."
        ),
      ],
      image: ASSETS?.churchEvent,
      caption: t("Vores fællesskab mødtes.", "Our community came together.", "Nuestra comunidad se unió."),
    },
    {
      kicker: t("KAPITEL 04", "CHAPTER 04", "CAPÍTULO 04"),
      title: t(
        "Den første handling blev til et fællesskab.",
        "The first action became a community.",
        "La primera acción se convirtió en una comunidad."
      ),
      body: [
        t(
          "I september 2026 mødte mere end 200 mennesker op til Social Run for Spain i Fælledparken. Det, der startede som endnu et indsamlingsevent, blev til et bevis på, at DANAmark kan samle mennesker gennem sport, kultur, mad og frivilligt arbejde.",
          "In September 2026, more than 200 people joined Social Run for Spain in Fælledparken. What began as another fundraising event became proof that DANAmark could bring people together through sport, culture, food and voluntary action.",
          "En septiembre de 2026, más de 200 personas se sumaron a la Social Run for Spain en Fælledparken. Lo que empezó como otro evento de recaudación se convirtió en la prueba de que DANAmark puede reunir a la gente a través del deporte, la cultura, la gastronomía y la acción voluntaria."
        ),
      ],
      image: "",
      caption: t("Menneskene bag DANAmark.", "The people behind DANAmark.", "Las personas detrás de DANAmark."),
    },
    {
      kicker: t("VORES FORMÅL", "OUR PURPOSE", "NUESTRO PROPÓSITO"),
      title: t(
        "Flere anledninger til, at Danmark og Spanien mødes.",
        "Creating more reasons for Denmark and Spain to meet.",
        "Crear más motivos para que Dinamarca y España se encuentren."
      ),
      body: [
        t(
          "DANAmark Solidaritet samler mennesker gennem fællesskab, kultur, sport og fælles handling. Solidaritet forbliver en central del af hvert større initiativ, og hvert event bidrager til en social eller humanitær sag.",
          "DANAmark Solidaritet brings people together through community, culture, sport and shared action. Solidarity remains part of every major initiative, with each event contributing to a social or humanitarian cause.",
          "DANAmark Solidaritet reúne a las personas a través de la comunidad, la cultura, el deporte y la acción compartida. La solidaridad se mantiene en el centro de cada gran iniciativa, y cada evento contribuye a una causa social o humanitaria."
        ),
      ],
      image: ASSETS?.paella,
      caption: t("Kulturen samler os også.", "Culture also brings people together.", "La cultura también une a las personas."),
      final: true,
    },
  ];

  // Adapt CMS section shape to the legacy render loop above. When the
  // CMS chapter has not uploaded its own image, use the per-chapter
  // approved asset by slug. If no slug match exists, leave the image
  // slot empty so the public page gracefully hides the image column
  // instead of repeating the same generic photograph.
  const CHAPTER_ASSET_BY_SLUG = {
    "chapter-01": { image: ASSETS?.planningMeeting, alt: { en: "Volunteers planning the first DANAmark initiative in Copenhagen",
                                                            da: "Frivillige planlægger DANAmarks første initiativ i København",
                                                            es: "Voluntarios planifican la primera iniciativa de DANAmark en Copenhague" } },
    "chapter-02": { image: ASSETS?.jerseys,          alt: { en: "Signed football shirts donated for the DANAmark initiative",
                                                            da: "Signerede fodboldtrøjer doneret til DANAmark-initiativet",
                                                            es: "Camisetas de fútbol firmadas donadas para la iniciativa DANAmark" } },
    "chapter-03": { image: ASSETS?.churchEvent,      alt: { en: "DANAmark community gathering inside the Catholic cathedral in Copenhagen",
                                                            da: "DANAmarks fællesskabsmøde i den katolske domkirke i København",
                                                            es: "Encuentro comunitario DANAmark en la catedral católica de Copenhague" } },
  };

  const chapters = cmsChapters.length > 0 ? cmsChapters.map((sec) => {
    const cmsImg = sec.images && sec.images[0];
    // Lookup an approved asset ONLY by exact slug match. No index-based
    // fallback, so paella.jpg never leaks into chapters it does not
    // belong to.
    const def = CHAPTER_ASSET_BY_SLUG[sec.slug] || null;
    const resolvedUrl = (cmsImg && cmsImg.url) || (def && def.image) || "";
    const resolvedAlt = (cmsImg && pickTx(cmsImg.alt, lang)) || (def && pickTx(def.alt, lang)) || "";
    return {
      kicker: pickTx(sec.kicker, lang),
      title: pickTx(sec.headline, lang),
      body: paragraphs(pickTx(sec.body, lang)),
      image: resolvedUrl,
      imageAlt: resolvedAlt,
      caption: pickTx(sec.caption, lang),
      final: !!sec.final,
      cta_label: pickTx(sec.cta_label, lang),
      cta_url: sec.cta_url || "/get-involved",
    };
  }) : legacyChapters.map((ch, idx) => {
    const def = Object.values(CHAPTER_ASSET_BY_SLUG)[idx];
    return { ...ch, imageAlt: def ? pickTx(def.alt, lang) : "" };
  });

  // Hero: prefer CMS values, fallback to defaults.
  const heroEyebrow = cmsHero ? pickTx(cmsHero.eyebrow, lang) : t("VORES HISTORIE", "OUR STORY", "NUESTRA HISTORIA");
  const heroHeadline = cmsHero ? pickTx(cmsHero.headline, lang) : t(
    "En bevægelse, der begyndte med én beslutning.",
    "A movement that began with one decision.",
    "Un movimiento que empezó con una decisión."
  );
  const heroImage = (cmsHero?.images && cmsHero.images[0]?.url) || missionHero.image;

  // Guard against any chapter without a valid definition or image
  const safeChapters = (chapters || []).filter(Boolean);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {heroImage ? (
          <img src={heroImage} alt={heroAlt} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-ink" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/50 to-transparent" />
        <div className="relative max-editorial container-x pb-20 pt-32 text-white">
          <div className="label-mono text-terracotta mb-5">{heroEyebrow}</div>
          <h1 className="font-editorial text-5xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tighter max-w-4xl">
            {heroHeadline}
          </h1>
        </div>
      </section>

      {safeChapters.map((ch, i) => (
        <section key={ch.kicker || i} className={`py-24 md:py-40 ${i % 2 === 1 ? "bg-ink text-white" : ""}`}>
          <div className="max-editorial container-x grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className={ch.image ? `md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}` : "md:col-span-8"}>
              <div className="label-mono text-terracotta">{ch.kicker}</div>
              <h2 className={`mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter ${ch.final ? "text-terracotta" : ""}`}>
                {ch.title}
              </h2>
              <div className={`mt-6 space-y-5 text-lg md:text-xl leading-relaxed ${i % 2 === 1 ? "text-white/85" : "text-ink/80"}`}>
                {(ch.body || []).map((p, j) => <p key={`${ch.title}-${j}-${p.slice(0, 24)}`}>{p}</p>)}
              </div>
              {ch.final && (
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href={ch.cta_url || "/get-involved"} className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-wide inline-flex items-center gap-2">
                    {ch.cta_label || t("Bliv en del af fællesskabet", "Get involved", "Colabora")} →
                  </a>
                </div>
              )}
            </div>
            {ch.image && (
            <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <figure>
                <img src={ch.image} alt={ch.imageAlt || ""} className="w-full aspect-[4/5] md:aspect-[5/6] object-cover" loading="lazy"
                       onError={(e) => {
                         // Hide gracefully if the image URL resolves to a non-image
                         // (e.g. the asset was removed and the server falls back to
                         // the SPA HTML). Prefer the empty placeholder <div> instead
                         // of showing a broken <img>.
                         const img = e.currentTarget;
                         if (img && img.parentNode) {
                           const placeholder = document.createElement("div");
                           placeholder.className = img.className.replace("object-cover", "") + ` ${i % 2 === 1 ? "bg-white/5" : "bg-ink/5"}`;
                           img.parentNode.replaceChild(placeholder, img);
                         }
                       }} />
                {ch.caption && (
                  <figcaption className={`mt-3 label-mono ${i % 2 === 1 ? "text-white/50" : "text-ink/50"}`}>
                    {ch.caption}
                  </figcaption>
                )}
              </figure>
            </div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

export function About() {
  const cms = useCmsPage("about");
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;
  usePageSEO(SEO.about.title, SEO.about.description, { cmsSeo: cms.seo });
  const [team, setTeam] = React.useState([]);
  React.useEffect(() => {
    import("@/lib/api").then(({ api }) => api.get("/content", { params: { kind: "person", status: "published" } }).then(r => setTeam(r.data)));
  }, []);

  // CMS-first: pull hero + org/contact + team-heading from the CMS when live.
  const cmsHero = cms.enabled ? cms.sections.find(s => s.slug === "hero") : null;
  const cmsOrg = cms.enabled ? cms.sections.find(s => s.slug === "organisation" && s.template === "pair") : null;
  const cmsTeam = cms.enabled ? cms.sections.find(s => s.slug === "team" && s.template === "team") : null;

  const eyebrow = (cmsHero && pickTx(cmsHero.eyebrow, lang)) || t("OM DANAMARK", "ABOUT DANAMARK", "SOBRE DANAMARK");
  const headline = (cmsHero && pickTx(cmsHero.headline, lang)) || t(
    "Menneskene og formålet bag DANAmark.",
    "The people and purpose behind DANAmark.",
    "Las personas y el propósito detrás de DANAmark."
  );
  const body = (cmsHero && pickTx(cmsHero.body, lang)) || t(
    "DANAmark er en dansk forening drevet af frivillige, som skaber forbindelser mellem mennesker i Danmark og Spanien. Vi bygger fællesskab gennem kultur, sport, mad og fælles handling.",
    "DANAmark is a Danish volunteer-led association connecting people across Denmark and Spain. We create community through culture, sport, food and shared action.",
    "DANAmark es una asociación danesa impulsada por voluntarios que conecta a personas de Dinamarca y España. Creamos comunidad a través de la cultura, el deporte, la gastronomía y la acción compartida."
  );
  const ctaImpactLabel  = (cmsHero && pickTx(cmsHero.cta_label, lang))  || t("Se vores resultater", "See our impact", "Descubre nuestro impacto");
  const ctaImpactUrl    = (cmsHero && cmsHero.cta_url) || "/impact";
  const ctaTranspLabel  = (cmsHero && pickTx(cmsHero.cta2_label, lang)) || t("Åbenhed", "Transparency", "Transparencia");
  const ctaTranspUrl    = (cmsHero && cmsHero.cta2_url) || "/legal/transparency";
  const ctaJoinLabel    = (cmsHero && pickTx(cmsHero.cta3_label, lang)) || t("Vær med", "Join DANAmark", "Únete a DANAmark");
  const ctaJoinUrl      = (cmsHero && cmsHero.cta3_url) || "/join";

  const orgLabel  = (cmsOrg && pickTx(cmsOrg.left_label, lang))  || t("Organisation", "Organisation", "Organización");
  const orgBody   = (cmsOrg && pickTx(cmsOrg.left_body, lang))   || t(
    "Dansk registreret forening\nCVR 45188981\nKøbenhavn, Danmark",
    "Registered Danish association\nCVR 45188981\nCopenhagen, Denmark",
    "Asociación danesa registrada\nCVR 45188981\nCopenhague, Dinamarca"
  );
  const contactLabel = (cmsOrg && pickTx(cmsOrg.right_label, lang)) || t("Kontakt", "Contact", "Contacto");
  const contactBody  = (cmsOrg && pickTx(cmsOrg.right_body, lang))  || "danamarksolidaritet@gmail.com";
  const contactHref  = (cmsOrg && cmsOrg.right_href) || "mailto:danamarksolidaritet@gmail.com";
  const teamHeading  = (cmsTeam && pickTx(cmsTeam.headline, lang)) || t("Menneskene bag.", "The people behind DANAmark.", "Las personas detrás.");

  return (
    <div className="container-x max-editorial py-24" data-testid="about-page">
      <div className="label-mono text-terracotta tracking-[0.2em] text-[10px]">{eyebrow}</div>
      <h1 className="font-editorial text-5xl md:text-7xl mt-3 tracking-tighter max-w-4xl">{headline}</h1>
      <div className="mt-10 text-ink/80 text-lg md:text-xl leading-relaxed max-w-3xl" data-testid="about-body">
        <RichText text={body} gapClassName="mt-5" />
      </div>

      <div className="mt-12 flex flex-wrap gap-3">
        <a href={ctaImpactUrl} data-testid="about-cta-impact"
           className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-wide">
          {ctaImpactLabel} →
        </a>
        <a href={ctaTranspUrl} data-testid="about-cta-transparency"
           className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-8 py-4 text-sm tracking-wide">
          {ctaTranspLabel}
        </a>
        <a href={ctaJoinUrl} data-testid="about-cta-join"
           className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-8 py-4 text-sm tracking-wide">
          {ctaJoinLabel}
        </a>
      </div>

      {team.length > 0 && (
        <section className="mt-24 border-t border-line pt-16">
          <h2 className="font-editorial text-4xl tracking-tighter">{teamHeading}</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-10">
            {team
              .filter(p => p && p.status !== "draft")
              .sort((a, b) => (a?.data?.order ?? 0) - (b?.data?.order ?? 0))
              .map(p => {
                const photo = p.data.photo || p.data.image || p.data.profile_image || "";
                // Strict per-language lookup — never silently fall back to
                // a different language. Missing EN/DA biography must stay
                // hidden until the admin provides a translation.
                const role = (typeof p.data.role === "string") ? p.data.role : ((p.data.role || {})[lang] || "");
                const bio = (typeof p.data.bio === "string") ? p.data.bio : ((p.data.bio || {})[lang] || "");
                const alt = (typeof p.data.alt === "string") ? p.data.alt : ((p.data.alt || {})[lang] || "");
                const focal = p.data.focal_point || { x: 50, y: 30 };
                return (
                  <div key={p.id} data-testid={`about-team-${p.id}`}>
                    {photo ? (
                      <div className="aspect-square overflow-hidden bg-line/10">
                        <img src={photo} alt={alt || p.data.name || ""} loading="lazy"
                             className="w-full h-full object-cover"
                             style={{ objectPosition: `${focal.x}% ${focal.y}%` }} />
                      </div>
                    ) : null}
                    <h3 className={`${photo ? "mt-4" : ""} font-editorial text-2xl`}>{p.data.name}</h3>
                    {role && <div className="label-mono text-ink/50 mt-1">{role}</div>}
                    {bio && (
                      <div className="mt-2 text-ink/70 text-sm leading-relaxed">
                        <RichText text={bio} gapClassName="mt-3" />
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </section>
      )}

      <section className="mt-24 border-t border-line pt-16 grid md:grid-cols-2 gap-10">
        <div>
          <div className="label-mono text-terracotta text-[10px] tracking-[0.2em]">{orgLabel}</div>
          <p className="mt-3 text-ink/80 text-lg leading-relaxed whitespace-pre-line">{orgBody}</p>
        </div>
        <div>
          <div className="label-mono text-terracotta text-[10px] tracking-[0.2em]">{contactLabel}</div>
          <p className="mt-3 text-ink/80 text-lg">
            <a href={contactHref} className="hover:text-terracotta">{contactBody}</a>
          </p>
        </div>
      </section>
    </div>
  );
}

export function Stories() {
  const cms = useCmsPage("stories");
  const [stories, setStories] = React.useState([]);
  const { pick, lang } = useI18n();
  // Stories has its own SEO (not the mission one). When the page is not
  // live in the CMS, add a `noindex,follow` robots meta so search engines
  // don't index an empty landing.
  const storiesSeoStatic = {
    title: { en: "Stories | DANAmark", da: "Historier | DANAmark", es: "Historias | DANAmark" },
    description: { en: "People, places and moments behind DANAmark.", da: "Menneskene, stederne og øjeblikkene bag DANAmark.", es: "Las personas, los lugares y los momentos detrás de DANAmark." },
  };
  usePageSEO(storiesSeoStatic.title, storiesSeoStatic.description, { cmsSeo: cms.seo });
  React.useEffect(() => {
    import("@/lib/api").then(({ api }) => api.get("/content", { params: { kind: "story", status: "published" } }).then(r => setStories(r.data)).catch(() => {}));
  }, []);
  const isLive = cms.enabled && stories.length > 0;
  React.useEffect(() => {
    // Toggle a robots meta tag on this page only. Removed on unmount.
    if (isLive) return;
    let m = document.querySelector('meta[name="robots"]');
    const created = !m;
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    m.setAttribute("content", "noindex,follow");
    return () => {
      if (created && m && m.parentNode) m.parentNode.removeChild(m);
      else if (m) m.removeAttribute("content");
    };
  }, [isLive]);
  const cmsHero = cms.enabled ? cms.sections.find(s => s.slug === "hero") : null;
  const eyebrow = (cmsHero && pickTx(cmsHero.eyebrow, lang)) || (lang === "es" ? "HISTORIAS" : lang === "en" ? "STORIES" : "HISTORIER");
  const headline = (cmsHero && pickTx(cmsHero.headline, lang)) || (lang === "es" ? "Desde el camino." : lang === "en" ? "From the road." : "Fra vejen.");
  const body = cmsHero && pickTx(cmsHero.body, lang);
  return (
    <div className="container-x max-editorial py-24">
      <div className="label-mono text-terracotta">{eyebrow}</div>
      <h1 className="font-editorial text-5xl md:text-7xl mt-3 tracking-tighter">{headline}</h1>
      {body && <p className="mt-6 text-ink/70 text-lg max-w-2xl leading-relaxed">{body}</p>}
      <div className="mt-16 grid md:grid-cols-2 gap-16">
        {stories.map(s => (
          <article key={s.id}>
            <div className="aspect-[4/3] overflow-hidden">
              <img src={s.data.cover} alt="" className="w-full h-full object-cover" />
            </div>
            <h2 className="mt-6 font-editorial text-3xl">{pick(s.data.title)}</h2>
            <p className="mt-3 text-ink/70">{pick(s.data.excerpt)}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

const IMPACT_PHOTOS = {
  laudrup:      "/images/laudrup.webp",
  climatePanel: "/images/climate-panel.webp",
  paella:       "/images/paella.jpg",
};

const PARTNER_LINKS = {
  fundacion: "https://fundacionisabelenriquediaz.org/",
  wck:       "https://wck.org/",
};

export function Impact() {
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;

  return (
    <div>
      <section className="container-x max-editorial pt-32 pb-20 md:pb-28">
        <div className="label-mono text-terracotta">{t("VORES AFTRYK", "OUR IMPACT", "IMPACTO")}</div>
        <h1 className="font-editorial text-5xl md:text-7xl lg:text-[92px] mt-4 tracking-tighter leading-[0.98] max-w-4xl">
          {t("Hvordan solidaritet blev til rigtig hjælp.", "How solidarity became real help.", "Cómo la solidaridad se convirtió en ayuda real.")}
        </h1>
        <p className="mt-6 max-w-2xl text-ink/70 text-lg leading-relaxed">
          {t("Fem kapitler. Ét fælles øjeblik.", "Five chapters. One shared moment.", "Cinco capítulos. Un mismo momento.")}
        </p>
      </section>

      <section className="border-t border-line">
        <img src={IMPACT_PHOTOS.laudrup} alt="Michael Laudrup with signed shirts" className="w-full aspect-[4/3] md:aspect-[16/9] object-cover" loading="lazy" />
        <div className="container-x max-editorial py-24 md:py-36 grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <div className="label-mono text-terracotta">{t("KAPITEL 01", "CHAPTER 01", "CAPÍTULO 01")}</div>
            <h2 className="mt-3 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Sporten forenet i solidaritet.", "Sport united for solidarity.", "El deporte, unido por la solidaridad.")}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8 text-lg leading-relaxed text-ink/80">
            <blockquote className="font-editorial text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.15] tracking-tight border-l-2 border-terracotta pl-8 py-2">
              {t("\"Nogle sejre måles ikke på tavlen, men på de liv, vi hjælper med at ændre.\"",
                 "\"Some victories are not measured on the scoreboard, but in the lives we help change.\"",
                 "\"Algunas victorias no se miden en el marcador, sino en las vidas que ayudamos a cambiar.\"")}
            </blockquote>
            <p>{t("Fodbold blev endnu et sprog for solidaritet. Klubber, professionelle spillere, Michael Laudrup og det spanske landshold donerede signerede trøjer til vores velgørenhedsauktion.",
                  "Football became another language of solidarity. Clubs, professional players, Michael Laudrup and the Spanish National Team donated signed shirts for our charity auction.",
                  "El fútbol se convirtió en otro idioma de la solidaridad. Clubes, jugadores profesionales, Michael Laudrup y la Selección Española donaron camisetas firmadas para nuestra subasta benéfica.")}</p>
            <p>{t("Det spanske landshold donerede signerede trøjer, da de besøgte København til en international kamp — og de trøjer blev til et af kampagnens højdepunkter, hvor sportslig gavmildhed forvandledes til reel humanitær støtte.",
                  "The Spanish National Team donated signed shirts when they visited Copenhagen for an international match — and those shirts became one of the highlights of the campaign, transforming sporting generosity into real humanitarian support.",
                  "La Selección Española donó camisetas firmadas cuando visitó Copenhague para un partido internacional — y esas camisetas se convirtieron en uno de los momentos clave de la campaña, transformando la generosidad deportiva en apoyo humanitario real.")}</p>
          </div>
        </div>
      </section>

      <section className="bg-ink text-white">
        <img src={IMPACT_PHOTOS.climatePanel} alt="Public discussion on climate and community resilience" className="w-full aspect-[16/9] md:aspect-[21/9] object-cover" loading="lazy" />
        <div className="container-x max-editorial py-24 md:py-36 grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <div className="label-mono text-terracotta">{t("KAPITEL 02", "CHAPTER 02", "CAPÍTULO 02")}</div>
            <h2 className="mt-3 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Viden forenet i solidaritet.", "Knowledge united for solidarity.", "El conocimiento, unido por la solidaridad.")}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-white/80">
            <p>{t("DANAmark Solidaritet handler ikke kun om at samle penge ind. Det handler også om at åbne øjne.",
                  "DANAmark Solidaritet is not only about raising money. It is also about raising awareness.",
                  "DANAmark Solidaritet no consiste solo en recaudar fondos. También se trata de despertar conciencias.")}</p>
            <p>{t("Under solidaritetsarrangementet blev der arrangeret en offentlig samtale om klimaforandringer og humanitære udfordringer. Eksperter og gæster reflekterede sammen over de klimarelaterede katastrofer, der bliver hyppigere — og over betydningen af modstandsdygtige fællesskaber.",
                  "During the solidarity event we hosted a public conversation on climate change and humanitarian challenges. Experts and guests reflected together on the growing impact of climate-related disasters — and on the importance of resilient communities.",
                  "Durante el evento solidario organizamos una conversación pública sobre el cambio climático y los retos humanitarios. Expertos e invitados reflexionaron juntos sobre el impacto creciente de las catástrofes climáticas — y sobre la importancia de las comunidades resilientes.")}</p>
            <p className="font-editorial text-2xl md:text-3xl text-white leading-snug pt-2">
              {t("Solidaritet bygges også gennem dialog, viden og fælles ansvar.",
                 "Solidarity is built through dialogue, knowledge and shared responsibility.",
                 "La solidaridad también se construye a través del diálogo, el conocimiento y la responsabilidad compartida.")}
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-line">
        <img src={IMPACT_PHOTOS.paella} alt="Community paella" className="w-full aspect-[4/3] md:aspect-[16/9] object-cover" loading="lazy" />
        <div className="container-x max-editorial py-24 md:py-36 grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <div className="label-mono text-terracotta">{t("KAPITEL 03", "CHAPTER 03", "CAPÍTULO 03")}</div>
            <h2 className="mt-3 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Fællesskabet forenet i solidaritet.", "Community united for solidarity.", "La comunidad, unida por la solidaridad.")}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-ink/80">
            <p>{t("Frivillige, virksomheder, familier og støtter mødtes omkring spansk gastronomi, kultur og et fælles kald. Hver ret, hver samtale, hvert håndtryk gjorde solidariteten synlig.",
                  "Volunteers, companies, families and supporters gathered around Spanish gastronomy, culture and a shared calling. Every dish, every conversation, every handshake made solidarity visible.",
                  "Voluntarios, empresas, familias y colaboradores se reunieron en torno a la gastronomía española, la cultura y una misma causa. Cada plato, cada conversación, cada apretón de manos hizo visible la solidaridad.")}</p>
          </div>
        </div>
      </section>

      <section className="border-t border-line">
        <div className="container-x max-editorial py-24 md:py-36">
          <div className="max-w-3xl">
            <div className="label-mono text-terracotta">{t("KAPITEL 04", "CHAPTER 04", "CAPÍTULO 04")}</div>
            <h2 className="mt-3 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {t("Solidariteten forvandlet til rigtig hjælp.", "Turning solidarity into real help.", "Convirtiendo la solidaridad en ayuda real.")}
            </h2>
            <p className="mt-8 text-lg md:text-xl text-ink/80 leading-relaxed">
              {t(
                "Vores fællesskab samlede over 200.000 DKK, og hver eneste krone blev leveret direkte til to betroede humanitære partnere, som allerede arbejdede tæt på de familier, der var ramt af oversvømmelserne i Valencia. I stedet for at bygge vores eget uddelingssystem valgte vi at støtte organisationer, som allerede var på jorden — og forvandlede solidaritet til øjeblikkelig, konkret hjælp.",
                "Our community raised more than 200,000 DKK, and every single krone was delivered directly to two trusted humanitarian partners already working closely with the families affected by the floods in Valencia. Instead of building our own aid system, we chose to support organisations already on the ground — turning solidarity into immediate, tangible help.",
                "Nuestra comunidad recaudó más de 200.000 DKK, y cada corona se entregó directamente a dos socios humanitarios de confianza que ya trabajaban junto a las familias afectadas por las inundaciones en Valencia. En lugar de crear nuestro propio sistema de distribución, elegimos apoyar a organizaciones que ya estaban sobre el terreno — transformando la solidaridad en ayuda real e inmediata."
              )}
            </p>
          </div>

          <div className="mt-20 md:mt-28 grid md:grid-cols-2 gap-8 md:gap-10">
            {/* Fundación Isabel Enrique Díaz — typography-only editorial card */}
            <article
              data-testid="partner-card-fundacion"
              className="border border-line hover:border-ink transition-colors p-10 md:p-14 flex flex-col"
            >
              <div className="label-mono text-terracotta" data-testid="partner-label-fundacion">
                {t("HUMANITÆR PARTNER", "HUMANITARIAN PARTNER", "SOCIO HUMANITARIO")}
              </div>
              <div aria-hidden className="mt-6 h-px w-16 bg-terracotta" />
              <h3 className="mt-6 font-editorial text-3xl md:text-4xl leading-[1.1] tracking-tight">
                Fundación Isabel Enrique Díaz
              </h3>
              <p className="mt-6 text-ink/75 text-lg leading-relaxed">
                {t(
                  "En del af donationen blev leveret til Fundación Isabel Enrique Díaz, som arbejdede direkte med DANA-ramte familier i Valencia. Fonden identificerede de mest presserende behov og sikrede, at støtten nåede frem til de mennesker, der havde allermest brug for den.",
                  "Part of the donation was delivered to Fundación Isabel Enrique Díaz, working directly with families affected by the DANA in Valencia. The foundation identified the most urgent needs and ensured that support reached the people who needed it most.",
                  "Una parte de la donación se entregó a la Fundación Isabel Enrique Díaz, que trabajó directamente con las familias afectadas por la DANA en Valencia. La fundación identificó las necesidades más urgentes y garantizó que el apoyo llegara a quienes más lo necesitaban."
                )}
              </p>
              <div className="mt-10 md:mt-auto md:pt-10">
                <a
                  href={PARTNER_LINKS.fundacion}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="partner-link-fundacion"
                  className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-7 py-3.5 text-sm tracking-wide transition-colors"
                >
                  {t("Besøg deres hjemmeside", "Visit their website", "Visita su web")} →
                </a>
              </div>
            </article>

            {/* World Central Kitchen — typography-only editorial card */}
            <article
              data-testid="partner-card-wck"
              className="border border-line hover:border-ink transition-colors p-10 md:p-14 flex flex-col"
            >
              <div className="label-mono text-terracotta" data-testid="partner-label-wck">
                {t("HUMANITÆR PARTNER", "HUMANITARIAN PARTNER", "SOCIO HUMANITARIO")}
              </div>
              <div aria-hidden className="mt-6 h-px w-16 bg-terracotta" />
              <h3 className="mt-6 font-editorial text-3xl md:text-4xl leading-[1.1] tracking-tight">
                World Central Kitchen
              </h3>
              <p className="mt-6 text-ink/75 text-lg leading-relaxed">
                {t(
                  "En del af donationen blev leveret til World Central Kitchen, grundlagt af kokken José Andrés. Organisationen serverede tusindvis af friskt tilberedte måltider og leverede akut fødevarehjælp under krisen — og gjorde hver donation til øjeblikkelig effekt.",
                  "Part of the donation was delivered to World Central Kitchen, founded by Chef José Andrés. The organisation served thousands of freshly prepared meals and delivered emergency food assistance during the crisis — turning every donation into immediate impact.",
                  "Una parte de la donación se entregó a World Central Kitchen, fundada por el chef José Andrés. La organización sirvió miles de comidas recién preparadas y ofreció ayuda alimentaria de emergencia durante la crisis — convirtiendo cada donación en impacto inmediato."
                )}
              </p>
              <div className="mt-10 md:mt-auto md:pt-10">
                <a
                  href={PARTNER_LINKS.wck}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="partner-link-wck"
                  className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-7 py-3.5 text-sm tracking-wide transition-colors"
                >
                  {t("Besøg deres hjemmeside", "Visit their website", "Visita su web")} →
                </a>
              </div>
            </article>
          </div>

          <div className="mt-20 md:mt-28 border-t border-line pt-12 md:pt-16 max-w-3xl">
            <p className="font-editorial text-2xl md:text-3xl leading-snug text-terracotta">
              {t("Hver donation havde en destination.", "Every donation had a destination.", "Cada donación tuvo un destino.")}
            </p>
            <p className="mt-5 label-mono text-ink/50">
              {t("Gennemsigtighed er en af DANAmark Solidaritets kerneværdier.",
                 "Transparency is one of DANAmark Solidaritet's core values.",
                 "La transparencia es uno de los valores fundamentales de DANAmark Solidaritet.")}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-terracotta text-white">
        <div className="container-x max-editorial py-28 md:py-48">
          <div className="max-w-4xl">
            <div className="label-mono text-white/80">{t("KAPITEL 05", "CHAPTER 05", "CAPÍTULO 05")}</div>
            <h2 className="mt-3 font-editorial text-4xl md:text-5xl lg:text-7xl leading-[1.02] tracking-tighter">
              {t("Vi er det næste kapitel.", "We are the next chapter.", "Nosotros somos el próximo capítulo.")}
            </h2>
            <p className="mt-10 text-white/95 text-xl md:text-2xl leading-[1.55] font-light">
              {t(
                "Solidaritet er ikke et øjeblik — det er en bevægelse, der vokser hver gang vi vælger at handle sammen. Frivillige, kunstnere, virksomheder, fodboldspillere, familier og hundredvis af mennesker forvandlede omsorg til reel hjælp for de familier, der var ramt af oversvømmelserne i Valencia. I 2026 fortsatte historien i Fælledparken med Social Run for Spain, og den fortsætter fortsat med hver ny anledning til, at Danmark og Spanien mødes.",
                "Solidarity is not a moment — it is a movement that grows every time we choose to act together. Volunteers, artists, companies, football players, families and hundreds of people turned care into real help for the families affected by the floods in Valencia. In 2026 the story continued in Fælledparken with Social Run for Spain, and it continues with every new reason for Denmark and Spain to meet.",
                "La solidaridad no es un momento — es un movimiento que crece cada vez que elegimos actuar juntos. Voluntarios, artistas, empresas, futbolistas, familias y cientos de personas convirtieron el cuidado en ayuda real para las familias afectadas por las inundaciones en Valencia. En 2026 la historia continuó en Fælledparken con la Social Run for Spain, y sigue con cada nueva ocasión para que Dinamarca y España se encuentren."
              )}
            </p>
            <div className="mt-14 flex flex-wrap gap-3">
              <a
                href="/events/social-run-for-spain"
                data-testid="impact-see-social-run-cta"
                className="inline-flex items-center gap-3 bg-white text-terracotta hover:bg-ink hover:text-white px-10 md:px-12 py-5 md:py-6 text-base md:text-lg tracking-wide font-medium transition-colors"
              >
                {t("Se hvad der skete", "See what happened", "Mira lo que pasó")} →
              </a>
              <a
                href="/join"
                className="inline-flex items-center gap-3 border border-white/70 text-white hover:bg-white hover:text-terracotta px-10 md:px-12 py-5 md:py-6 text-base md:text-lg tracking-wide font-medium transition-colors"
              >
                {t("Vær med", "Join DANAmark", "Únete a DANAmark")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


/* ────────────────────────────  Join the Movement  ────────────────────────────
 * Photograph-free by design. Visual rhythm comes from typography, whitespace
 * and elegant editorial cards — not more images.
 */
function useLocaleT() {
  const { lang } = useI18n();
  return (da, en, es) => ({ da, en, es })[lang] || en;
}

export function GetInvolved() {
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;
  const cms = useCmsPage("get_involved");
  usePageSEO({ da: "Bliv en del af os | DANAmark Solidaritet", en: "Get Involved | DANAmark Solidaritet", es: "Únete | DANAmark Solidaritet" },
             { da: "Bliv medlem, frivillig, samarbejdspartner eller støtte af DANAmark Solidaritet.", en: "Join DANAmark as a member, volunteer, collaborator or supporter.", es: "Únete a DANAmark como socio, voluntario, colaborador o apoyo." },
             { cmsSeo: cms.seo });
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [interestModal, setInterestModal] = useState(null); // "volunteer" | "company" | null

  // CMS sections looked up by slug — falls back to defaults when unavailable.
  const heroSec = cms.enabled ? cms.sections.find(s => s.slug === "hero") : null;
  const communitySec = cms.enabled ? cms.sections.find(s => s.slug === "community") : null;
  const partnersSec = cms.enabled ? cms.sections.find(s => s.slug === "partners_grid") : null;
  const ctaSec = cms.enabled ? cms.sections.find(s => s.slug === "final_cta") : null;
  const cmsPick = (obj) => pickTx(obj, lang);

  useEffect(() => {
    api.get("/content", { params: { kind: "collaborator", status: "published" } })
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(e => { setErr(String(e?.message || e)); setRows([]); });
  }, []);

  // Deep-link support for the Home partners carousel: when the page loads
  // with a hash like `#partner-lima-empanadas`, scroll the matching card
  // into view and apply a subtle 2-second highlight so the visitor sees
  // exactly which partner they clicked. Runs once rows are rendered.
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash || !hash.startsWith("partner-")) return;
    // Give the DOM a tick to render the cards before scrolling.
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("partner-anchor-highlight");
      setTimeout(() => el.classList.remove("partner-anchor-highlight"), 2200);
    }, 120);
    return () => clearTimeout(timer);
  }, [rows]);

  // Featured (tier=main) → first, then supporter, then friend; within each, respect order asc,
  // then fall back to alphabetical name so ordering is fully deterministic.
  const TIER_RANK = { main: 0, supporter: 1, friend: 2 };
  const collaborators = useMemo(() => {
    const active = (rows || [])
      .filter(it => it?.data?.active !== false)
      .sort((a, b) => {
        const ra = TIER_RANK[a?.data?.tier] ?? 1;
        const rb = TIER_RANK[b?.data?.tier] ?? 1;
        if (ra !== rb) return ra - rb;
        const oa = a?.data?.order ?? 99;
        const ob = b?.data?.order ?? 99;
        if (oa !== ob) return oa - ob;
        return (a?.data?.name || "").localeCompare(b?.data?.name || "");
      });
    return active;
  }, [rows]);

  // Fallback chain: current language → EN → company name only.
  const pick = (obj, key) => (obj?.[key]?.[lang] || obj?.[key]?.en || "").trim();

  const scrollToCta = () => {
    const el = document.getElementById("collab-cta");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const CONTACT_MAILTO = "mailto:danamarksolidaritet@gmail.com?subject=Collaboration with DANAmark Solidaritet";

  const volunteerSubject = t(
    "Jeg vil gerne være frivillig hos DANAmark Solidaritet",
    "I would like to volunteer with DANAmark Solidaritet",
    "Quiero ser voluntario/a en DANAmark Solidaritet"
  );
  const volunteerBody = t(
    "Hej,\n\nJeg vil gerne hjælpe som frivillig hos DANAmark Solidaritet.\n\nMit navn:\nMit telefonnummer:\nSprog jeg taler:\nJeg vil gerne hjælpe med:\nMin omtrentlige tilgængelighed:\n\nVenlig hilsen",
    "Hello,\n\nI would like to volunteer with DANAmark Solidaritet.\n\nMy name:\nMy phone number:\nLanguages I speak:\nI would like to help with:\nMy approximate availability:\n\nKind regards",
    "Hola,\n\nMe gustaría colaborar como voluntario/a con DANAmark Solidaritet.\n\nMi nombre es:\nMi teléfono:\nIdiomas que hablo:\nMe gustaría ayudar con:\nMi disponibilidad aproximada:\n\nUn saludo"
  );
  const companySubject = t(
    "Min virksomhed vil gerne samarbejde med DANAmark Solidaritet",
    "My company would like to collaborate with DANAmark Solidaritet",
    "Mi empresa quiere colaborar con DANAmark Solidaritet"
  );
  const companyBody = t(
    "Hej,\n\nMin virksomhed vil gerne samarbejde med DANAmark Solidaritet.\n\nKontaktpersonens navn:\nVirksomhedens navn:\nTelefonnummer:\nHjemmeside eller sociale medier:\nType samarbejde vi foreslår:\nKort besked:\n\nVenlig hilsen",
    "Hello,\n\nMy company would like to collaborate with DANAmark Solidaritet.\n\nContact person's name:\nCompany name:\nPhone number:\nWebsite or social media:\nType of collaboration proposed:\nShort message:\n\nKind regards",
    "Hola,\n\nMi empresa quiere colaborar con DANAmark Solidaritet.\n\nNombre de la persona de contacto:\nNombre de la empresa:\nTeléfono:\nSitio web o redes sociales:\nTipo de colaboración propuesta:\nMensaje corto:\n\nUn saludo"
  );
  const buildMailto = (subject, body) =>
    `mailto:danamarksolidaritet@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const VOLUNTEER_MAILTO = buildMailto(volunteerSubject, volunteerBody);
  const COMPANY_MAILTO = buildMailto(companySubject, companyBody);

  return (
    <div>
      {/* ─────────── HERO ─────────── */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-ink" data-testid="collab-hero">
        <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #E85A3A 0, transparent 55%), radial-gradient(circle at 80% 70%, #E85A3A 0, transparent 60%)"
        }} />
        <div className="relative container-x max-editorial pt-32 pb-16 md:pb-24 text-white">
          <motion.div {...fade}>
            <div className="label-mono text-terracotta mb-6" data-testid="collab-hero-kicker">
              {cmsPick(heroSec?.eyebrow) || t("SAMARBEJDSPARTNERE", "COLLABORATING COMPANIES", "EMPRESAS COLABORADORAS")}
            </div>
            <h1 data-testid="collab-hero-title"
                className="font-editorial text-5xl md:text-7xl lg:text-[92px] tracking-tighter leading-[0.96] max-w-5xl">
              {cmsPick(heroSec?.headline) || t(
                "Virksomheder, der gør solidariteten mulig.",
                "Companies that make solidarity possible.",
                "Empresas que hacen posible la solidaridad."
              )}
            </h1>
            <p data-testid="collab-hero-sub"
               className="mt-8 md:mt-10 text-lg md:text-2xl text-white/90 max-w-3xl leading-[1.55] font-light whitespace-pre-line">
              {cmsPick(heroSec?.body) || t(
                "Vores arrangementer er mulige takket være virksomheder, der bidrager med produkter, tjenester, præmier, mad, logistik og økonomisk støtte. Hvert bidrag hjælper os med at omsætte idéer til reel handling.",
                "Our events are possible thanks to companies that contribute with products, services, prizes, food, logistics and financial support. Every contribution helps us turn solidarity into real action.",
                "Nuestros eventos son posibles gracias a empresas que aportan productos, servicios, premios, comida, logística y apoyo económico. Cada aportación nos ayuda a convertir la solidaridad en acción real."
              )}
            </p>
            <div className="mt-12">
              <button type="button" onClick={scrollToCta} data-testid="collab-hero-cta"
                      className="inline-flex items-center gap-3 bg-terracotta hover:bg-terracotta-dark text-white px-10 py-5 text-sm md:text-base tracking-wide transition-colors">
                {cmsPick(heroSec?.cta_label) || t("Bliv samarbejdspartner", "Become a collaborator", "Conviértete en colaborador")} →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─────────── VOLUNTEER & COMMUNITY ─────────── */}
      <section className="container-x max-editorial py-24 md:py-32" data-testid="community-section">
        <motion.div {...fade} className="grid md:grid-cols-[1.15fr,1fr] gap-10 md:gap-16 items-center">
          <div>
            <div className="label-mono text-terracotta">{cmsPick(communitySec?.eyebrow) || t("FÆLLESSKAB", "COMMUNITY", "COMUNIDAD")}</div>
            <h2 data-testid="community-title"
                className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {cmsPick(communitySec?.headline) || t(
                "Almindelige mennesker. Ekstraordinære handlinger.",
                "Ordinary people. Extraordinary actions.",
                "Personas normales. Actos extraordinarios."
              )}
            </h2>
            <div className="mt-8 space-y-4 text-ink/80 text-base md:text-lg leading-relaxed max-w-xl whitespace-pre-line">
              {cmsPick(communitySec?.body) ? (
                <p>{cmsPick(communitySec?.body)}</p>
              ) : (<>
              <p>{t(
                "DANAmark Solidaritet består af helt almindelige mennesker, som sammen skaber ekstraordinære resultater. Der findes ikke kun én måde at hjælpe på.",
                "DANAmark Solidaritet is built by ordinary people who come together to achieve extraordinary things. There is no single way to help.",
                "DANAmark Solidaritet está formado por personas normales que, cuando se unen, consiguen hacer cosas extraordinarias. No hay una única forma de ayudar."
              )}</p>
              <p>{t(
                "Nogle deltager i vores arrangementer. Andre hjælper som frivillige. Nogle virksomheder sponsorerer, donerer produkter eller bidrager med deres ydelser. Andre vælger at støtte økonomisk.",
                "Some people take part in our events. Others volunteer. Some companies sponsor us, donate products or contribute their services. Others choose to support us financially.",
                "Algunas personas participan en nuestros eventos. Otras colaboran como voluntarias. Hay empresas que patrocinan, donan productos o aportan servicios. Otras personas prefieren contribuir económicamente."
              )}</p>
              <p>{t(
                "Alle bidrag gør en forskel. Sammen kan vi skabe noget, der er meget større end os selv.",
                "Every contribution matters. Together, we can create something much bigger than ourselves.",
                "Cada ayuda cuenta. Juntos podemos crear algo mucho más grande que nosotros mismos."
              )}</p>
              </>)}
            </div>
          </div>
          <div className="flex flex-col gap-4 md:pl-8">
            <button type="button" onClick={() => setInterestModal("volunteer")} data-testid="community-volunteer-btn"
                    className="inline-flex items-center justify-between gap-3 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-5 text-base md:text-lg tracking-wide transition-colors rounded-md text-left">
              <span>{cmsPick(communitySec?.volunteer_label) || t("Jeg vil gerne være frivillig", "I want to volunteer", "Quiero ser voluntario/a")}</span>
              <span aria-hidden>→</span>
            </button>
            <button type="button" onClick={() => setInterestModal("company")} data-testid="community-company-btn"
                    className="inline-flex items-center justify-between gap-3 border border-ink text-ink hover:bg-ink hover:text-white px-8 py-5 text-base md:text-lg tracking-wide transition-colors rounded-md text-left">
              <span>{cmsPick(communitySec?.company_label) || t("Jeg vil samarbejde som virksomhed", "I want to collaborate as a company", "Quiero colaborar como empresa")}</span>
              <span aria-hidden>→</span>
            </button>
          </div>
        </motion.div>
      </section>

      {interestModal && (
        <InterestModal kind={interestModal} lang={lang} onClose={() => setInterestModal(null)} />
      )}

      {/* ─────────── COLLABORATORS GRID ─────────── */}
      <section id="partners" className="scroll-mt-24 container-x max-editorial py-24 md:py-32" data-testid="collab-grid-section">
        <motion.div {...fade} className="max-w-3xl">
          <div className="label-mono text-terracotta">{cmsPick(partnersSec?.eyebrow) || t("AKTUELLE SAMARBEJDSPARTNERE", "CURRENT COLLABORATORS", "COLABORADORES ACTUALES")}</div>
          <h2 className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
            {cmsPick(partnersSec?.headline) || t("Sammen når vi længere.", "Together we go further.", "Juntos llegamos más lejos.")}
          </h2>
        </motion.div>

        {rows === null ? (
          <div data-testid="collab-loading" className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map(i => (
              <div key={i} className="border border-line rounded-xl p-6 animate-pulse">
                <div className="w-24 h-24 bg-line/40 rounded" />
                <div className="mt-4 h-4 w-2/3 bg-line/40" />
                <div className="mt-3 h-3 w-full bg-line/30" />
                <div className="mt-2 h-3 w-4/5 bg-line/30" />
              </div>
            ))}
          </div>
        ) : collaborators.length === 0 ? (
          <div data-testid="collab-empty"
               className="mt-16 border border-line p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="font-editorial text-2xl md:text-3xl leading-snug tracking-tight">
                {t("Det næste logo kunne være dit.", "The next logo could be yours.", "El próximo logotipo podría ser el tuyo.")}
              </p>
              <p className="mt-3 text-ink/70 leading-relaxed">
                {t(
                  "Bliv en del af et fællesskab, der omsætter solidaritet til reel hjælp.",
                  "Join a community that transforms solidarity into real support.",
                  "Forma parte de una comunidad que transforma la solidaridad en ayuda real."
                )}
              </p>
            </div>
            <a href={CONTACT_MAILTO} data-testid="collab-empty-cta"
               className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-7 py-3.5 text-sm tracking-wide transition-colors whitespace-nowrap">
              {t("Bliv samarbejdspartner", "Become a collaborator", "Conviértete en colaborador")} →
            </a>
          </div>
        ) : (
          <div data-testid="collab-grid" className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collaborators.map(it => {
              const name = it.data?.name || it.data?.slug || "";
              const desc = pick(it.data, "description") || name;
              const contrib = pick(it.data, "contribution");
              const link = (it.data?.website || it.data?.instagram || "").trim();
              const isFeatured = it.data?.tier === "main";
              const CardTag = link ? "a" : "article";
              const linkProps = link ? { href: link, target: "_blank", rel: "noopener noreferrer" } : {};
              // Preserve slug-based anchor for Home carousel deep-links.
              // Falls back to id when a slug is missing so we never lose a
              // partner. Both anchors are attached in parallel so old bookmarks
              // that used the id form (if any) keep working.
              const slug = (it.data?.slug || "").trim();
              const anchorSlug = slug ? `partner-${slug}` : null;
              const anchorId = `partner-id-${it.id}`;
              return (
                <CardTag key={it.id} {...linkProps}
                         id={anchorSlug || anchorId}
                         data-partner-anchor={anchorSlug || anchorId}
                         data-testid={`collab-card-${it.id}`}
                         aria-label={link ? `${name} — ${t("besøg hjemmeside", "visit website", "visitar sitio web")}` : name}
                         className={`group border rounded-xl p-6 bg-base flex flex-col text-left transition-all ${link ? "hover:border-terracotta hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-terracotta/40" : "border-line"} ${isFeatured ? "border-terracotta/60" : "border-line"}`}>
                  {isFeatured && (
                    <span data-testid={`collab-featured-${it.id}`}
                          className="label-mono text-[10px] text-terracotta mb-3">
                      ★ {t("Fremhævet samarbejdspartner", "Featured collaborator", "Colaborador destacado")}
                    </span>
                  )}
                  <div className="h-24 flex items-center justify-start">
                    {it.data?.logo ? (
                      <SafeImg src={it.data.logo} alt={name} name={name}
                               className="max-h-24 max-w-[70%] object-contain"
                               fallbackClassName="h-24 w-full py-6 px-4 text-center" />
                    ) : (
                      <div className="w-full border border-dashed border-line/60 rounded-lg py-6 px-4 text-center">
                        <div className="font-editorial text-lg text-ink/70">{name}</div>
                      </div>
                    )}
                  </div>
                  <h3 className="mt-6 font-editorial text-xl tracking-tight leading-tight">{name}</h3>
                  {desc && desc !== name && (
                    <div className="mt-3 text-sm text-ink/70 leading-relaxed">
                      <RichText text={desc} gapClassName="mt-3" />
                    </div>
                  )}
                  {contrib && (
                    <p className="mt-3 label-mono text-[10px] text-ink/50">
                      {t("BIDRAG", "CONTRIBUTION", "APORTACIÓN")}: {contrib}
                    </p>
                  )}
                  {link && (
                    <span className="mt-auto pt-5 label-mono text-xs text-terracotta group-hover:text-terracotta-dark">
                      {t("Besøg virksomheden", "Visit company", "Visitar empresa")} →
                    </span>
                  )}
                </CardTag>
              );
            })}
          </div>
        )}
        {err && <p className="mt-6 text-sm text-red-600" data-testid="collab-error">{t("Der opstod en fejl.", "Something went wrong.", "Ha ocurrido un error.")}</p>}
      </section>

      {/* ─────────── CTA FOR NEW COMPANIES ─────────── */}
      <section id="collab-cta" className="bg-terracotta text-white" data-testid="collab-cta-section">
        <div className="container-x max-editorial py-24 md:py-36">
          <div className="max-w-4xl">
            <div className="label-mono text-white/80">{cmsPick(ctaSec?.eyebrow) || t("BLIV EN DEL", "JOIN US", "ÚNETE")}</div>
            <h2 data-testid="collab-cta-title"
                className="mt-4 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              {cmsPick(ctaSec?.headline) || t(
                "Vil din virksomhed være med til at hjælpe?",
                "Would your company like to help?",
                "¿Le gustaría a tu empresa ayudar?"
              )}
            </h2>
            <p className="mt-8 text-white/95 text-lg md:text-xl leading-[1.55] font-light max-w-3xl whitespace-pre-line">
              {cmsPick(ctaSec?.body) || t(
                "Virksomheder kan samarbejde ved at donere produkter eller lodtrækningspræmier, tilbyde tjenester, sponsorere en del af et arrangement eller støtte os økonomisk. Vi tilpasser hvert samarbejde til virksomhedens muligheder og giver synlighed til de virksomheder, der gør vores arbejde muligt.",
                "Companies can collaborate by donating products or raffle prizes, providing services, sponsoring part of an event or supporting us financially. We adapt each collaboration to the possibilities of every business and give visibility to the companies that make our work possible.",
                "Las empresas pueden colaborar donando productos o premios para la rifa, ofreciendo servicios, patrocinando parte de un evento o apoyándonos económicamente. Adaptamos cada colaboración a las posibilidades de cada empresa y damos visibilidad a las que hacen posible nuestro trabajo."
              )}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a href={(ctaSec?.cta_url) || CONTACT_MAILTO} data-testid="collab-cta-btn"
                 className="inline-flex items-center gap-3 bg-white text-terracotta hover:bg-ink hover:text-white px-10 md:px-12 py-5 md:py-6 text-base md:text-lg tracking-wide font-medium transition-colors">
                {cmsPick(ctaSec?.cta_label) || t("Kontakt os for at samarbejde", "Contact us to collaborate", "Contáctanos para colaborar")} →
              </a>
              <Link to={(ctaSec?.cta2_url) || "/partnership"} data-testid="collab-cta-form"
                    className="inline-flex items-center gap-3 border border-white text-white hover:bg-white hover:text-terracotta px-10 md:px-12 py-5 md:py-6 text-base md:text-lg tracking-wide font-medium transition-colors">
                {cmsPick(ctaSec?.cta2_label) || t("Åbn kontaktformular", "Open contact form", "Abrir formulario de contacto")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InterestModal({ kind, lang, onClose }) {
  const t = (da, en, es) => ({ da, en, es })[lang] || en;
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const isVol = kind === "volunteer";
  const title = isVol
    ? t("Jeg vil gerne være frivillig", "I want to volunteer", "Quiero ser voluntario/a")
    : t("Jeg vil samarbejde som virksomhed", "I want to collaborate as a company", "Quiero colaborar como empresa");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.name || !f.email) return setErr(t("Navn og e-mail er påkrævet.", "Name and email are required.", "El nombre y el email son obligatorios."));
    setBusy(true);
    try {
      await api.post("/community/interest", { ...f, kind, language: lang });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || t("Der opstod en fejl. Prøv igen.", "Something went wrong. Please try again.", "Ha ocurrido un error. Inténtalo de nuevo."));
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <div className="bg-base w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto rounded-md"
           onClick={e => e.stopPropagation()} data-testid={`interest-modal-${kind}`}>
        <div className="flex items-baseline justify-between gap-4 mb-6">
          <div>
            <div className="label-mono text-terracotta text-[10px]">
              {isVol ? t("FRIVILLIG", "VOLUNTEER", "VOLUNTARIADO") : t("VIRKSOMHED", "COMPANY", "EMPRESA")}
            </div>
            <h3 className="font-editorial text-2xl md:text-3xl mt-1">{title}</h3>
          </div>
          <button onClick={onClose} className="label-mono text-ink/50" aria-label="Close">✕</button>
        </div>
        {done ? (
          <div data-testid="interest-done" className="space-y-4">
            <p className="text-lg">{t("Tak — vi har modtaget din besked!", "Thank you — we've received your message!", "¡Gracias — hemos recibido tu mensaje!")}</p>
            <p className="text-sm text-ink/70">{t(
              "Vi har sendt dig en bekræftelse. En fra teamet vender tilbage inden for få dage.",
              "We've sent you a confirmation email. Someone from the team will reply within a few days.",
              "Te hemos enviado un email de confirmación. Alguien del equipo te responderá en unos días."
            )}</p>
            <button onClick={onClose} className="mt-2 border border-ink px-6 py-3 text-sm">{t("Luk", "Close", "Cerrar")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 text-sm">
            <IF label={t("Navn", "Name", "Nombre")} v={f.name || ""} on={v => set("name", v)} testid="if-name" required />
            <IF label={t("E-mail", "Email", "Email")} type="email" v={f.email || ""} on={v => set("email", v)} testid="if-email" required />
            <IF label={t("Telefon", "Phone", "Teléfono")} v={f.phone || ""} on={v => set("phone", v)} testid="if-phone" />
            {isVol ? (
              <>
                <IF label={t("Sprog jeg taler", "Languages I speak", "Idiomas que hablo")} v={f.languages || ""} on={v => set("languages", v)} testid="if-langs" />
                <IF label={t("Jeg vil gerne hjælpe med", "I would like to help with", "Me gustaría ayudar con")} v={f.help_with || ""} on={v => set("help_with", v)} testid="if-help" />
                <IF label={t("Min omtrentlige tilgængelighed", "My approximate availability", "Mi disponibilidad aproximada")} v={f.availability || ""} on={v => set("availability", v)} testid="if-avail" />
              </>
            ) : (
              <>
                <IF label={t("Virksomhedens navn", "Company name", "Nombre de la empresa")} v={f.company_name || ""} on={v => set("company_name", v)} testid="if-company" required />
                <IF label={t("Hjemmeside eller sociale medier", "Website or social media", "Sitio web o redes sociales")} v={f.website || ""} on={v => set("website", v)} testid="if-web" />
                <IF label={t("Type samarbejde", "Type of collaboration", "Tipo de colaboración")} v={f.collab_type || ""} on={v => set("collab_type", v)} testid="if-type" />
              </>
            )}
            <div>
              <label className="label-mono text-ink/60 text-[10px]">{t("Kort besked", "Short message", "Mensaje corto")}</label>
              <textarea rows="3" value={f.message || ""} onChange={e => set("message", e.target.value)} data-testid="if-msg"
                        className="mt-2 w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent" />
            </div>
            {err && <div data-testid="if-error" className="text-sm text-red-600">{err}</div>}
            <div className="pt-2 flex flex-wrap gap-3">
              <button type="submit" disabled={busy} data-testid="if-submit"
                      className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-6 py-3 text-sm tracking-wide rounded-md">
                {busy ? "…" : t("Send", "Send", "Enviar")}
              </button>
              <button type="button" onClick={onClose} className="border border-line text-ink/70 px-6 py-3 text-sm">
                {t("Annuller", "Cancel", "Cancelar")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function IF({ label, v, on, testid, type = "text", required }) {
  return (
    <div>
      <label className="label-mono text-ink/60 text-[10px]">{label}{required ? " *" : ""}</label>
      <input type={type} required={required} value={v} onChange={e => on(e.target.value)} data-testid={testid}
             className="mt-1 w-full border border-line focus:border-terracotta outline-none px-3 py-2 bg-transparent" />
    </div>
  );
}


export function Partnership() {
  const { lang, t } = useI18n();
  const tx = (da, en, es) => ({ da, en, es })[lang] || en;
  const cms = useCmsPage("partnership");
  usePageSEO({}, {}, { cmsSeo: cms.seo });
  const hero = cms.enabled ? cms.sections.find(s => s.slug === "hero") : null;
  const kicker = pickTx(hero?.eyebrow, lang) || tx("PARTNERSKAB", "PARTNERSHIP", "COLABORACIÓN");
  const title = pickTx(hero?.headline, lang) || tx("Byg med os.", "Build with us.", "Construye con nosotros.");
  const intro = pickTx(hero?.body, lang) || tx(
    "Vi samarbejder med virksomheder, der ønsker at skabe forbindelser mellem Danmark og Spanien. Fortæl os om jer, så vender vi tilbage.",
    "We work with companies that want to build connections between Denmark and Spain. Tell us about you and we will get back to you.",
    "Trabajamos con empresas que quieren tejer puentes entre Dinamarca y España. Cuéntanos sobre vosotros y te responderemos."
  );
  const [f, setF] = React.useState({ name: "", email: "", organization: "", message: "" });
  const [sent, setSent] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const { api } = await import("@/lib/api");
    await api.post("/inquiries", { ...f, kind: "partnership" });
    setSent(true);
  };
  if (sent) return <SponsorThankYou />;
  return (
    <div className="container-x max-editorial py-24 max-w-2xl">
      <div className="label-mono text-terracotta" data-testid="partnership-kicker">
        {kicker}
      </div>
      <h1 className="font-editorial text-5xl md:text-6xl mt-3 tracking-tighter" data-testid="partnership-title">
        {title}
      </h1>
      <p className="mt-6 text-ink/70 text-lg leading-relaxed" data-testid="partnership-intro">
        {intro}
      </p>
      <form onSubmit={submit} data-testid="partnership-form" className="mt-12 space-y-6">
        <input required placeholder={tx("Navn", "Name", "Nombre")}
               value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="p-name"
               className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        <input required type="email" placeholder={tx("E-mail", "Email", "Email")}
               value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="p-email"
               className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        <input placeholder={tx("Organisation", "Organization", "Organización")}
               value={f.organization} onChange={e => setF({ ...f, organization: e.target.value })} data-testid="p-org"
               className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        <textarea required rows="5" placeholder={tx("Fortæl os om jer", "Tell us about you", "Cuéntanos sobre vosotros")}
                  value={f.message} onChange={e => setF({ ...f, message: e.target.value })} data-testid="p-message"
                  className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
        <button data-testid="p-submit"
                className="bg-terracotta text-white px-8 py-3.5 text-sm tracking-wide hover:bg-terracotta-dark">
          {tx("Send", "Send", "Enviar")}
        </button>
      </form>
    </div>
  );
}

function SponsorThankYou() {
  const { t } = useI18n();
  const [downloading, setDownloading] = React.useState(false);
  const download = () => {
    // Partnership Book PDF will be published to /partnership-book.pdf once designed.
    setDownloading(true);
    setTimeout(() => setDownloading(false), 1200);
  };
  return (
    <div data-testid="partnership-sent" className="container-x max-editorial py-24 md:py-40 max-w-3xl">
      <div className="label-mono text-terracotta">{t("sponsor_thanks_kicker")}</div>
      <h1 className="font-editorial text-5xl md:text-6xl lg:text-7xl mt-4 tracking-tighter leading-[1.02]">{t("sponsor_thanks_title")}</h1>
      <p className="mt-6 text-lg text-ink/75 max-w-xl leading-relaxed">{t("sponsor_thanks_body")}</p>
      <div className="mt-12 border border-line p-8 md:p-10 grid md:grid-cols-[1fr,auto] gap-6 items-center">
        <div>
          <div className="label-mono text-ink/60">Partnership Book · 2026</div>
          <div className="font-editorial text-2xl md:text-3xl mt-2">DANAmark Solidaritet</div>
          <div className="mt-1 text-ink/60 text-sm">{t("sponsor_download_soon")}</div>
        </div>
        <button onClick={download} disabled={downloading} data-testid="sponsor-download"
                className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-8 py-4 text-sm tracking-wide inline-flex items-center gap-2">
          {downloading ? "…" : t("sponsor_download")}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────  Collaborators (dedicated page)  ──────────────────────────── */
function pickLocale(obj, lang) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.da || obj.es || "";
}

export function Collaborators() {
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;
  const [items, setItems] = useState([]);
  const location = useLocation();

  useEffect(() => {
    import("@/lib/api").then(({ api }) =>
      api.get("/content", { params: { kind: "collaborator" } })
        .then((r) => setItems(r.data || []))
        .catch(() => {})
    );
  }, []);

  const TIER_RANK = { main: 0, supporter: 1, friend: 2 };
  const active = items
    .filter((x) => x?.data?.active !== false)
    .sort((a, b) => {
      const ta = TIER_RANK[a?.data?.tier] ?? 1;
      const tb = TIER_RANK[b?.data?.tier] ?? 1;
      if (ta !== tb) return ta - tb;
      return (a?.data?.order ?? 0) - (b?.data?.order ?? 0);
    });

  useEffect(() => {
    if (!location.hash || active.length === 0) return;
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, active.length]);

  return (
    <div>
      <section className="border-b border-line">
        <div className="container-x max-editorial py-32 md:py-48 max-w-4xl">
          <div className="label-mono text-terracotta" data-testid="collab-page-kicker">
            {t("SAMARBEJDSPARTNERE", "COLLABORATORS", "COLABORADORES")}
          </div>
          <h1
            data-testid="collab-page-title"
            className="mt-4 font-editorial text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter"
          >
            {t("Vores samarbejdspartnere", "Our Collaborators", "Nuestros colaboradores")}
          </h1>
          <p
            data-testid="collab-page-subtitle"
            className="mt-8 text-lg md:text-xl text-ink/70 leading-relaxed max-w-3xl"
          >
            {t(
              "Denne begivenhed ville ikke være mulig uden de virksomheder og organisationer, som tror på fællesskab og vælger at gøre en forskel sammen med os.",
              "This event would not be possible without the companies and organisations that believe in community and choose to make a difference together with us.",
              "Este proyecto no sería posible sin las empresas y organizaciones que creen en la comunidad y deciden formar parte de este movimiento. Gracias por hacerlo posible."
            )}
          </p>
        </div>
      </section>

      <section className="container-x max-editorial py-24 md:py-32">
        {active.length > 0 ? (
          <div
            data-testid="collab-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
          >
            {active.map((c) => (
              <CollaboratorCard key={c.id} item={c} lang={lang} t={t} />
            ))}
          </div>
        ) : (
          <div
            data-testid="collab-empty"
            className="border border-line p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8 max-w-4xl mx-auto"
          >
            <div>
              <p className="font-editorial text-2xl md:text-3xl leading-snug tracking-tight">
                {t(
                  "Det næste logo kunne være dit.",
                  "The next logo could be yours.",
                  "El próximo logotipo podría ser el tuyo."
                )}
              </p>
              <p className="mt-3 text-ink/70 leading-relaxed">
                {t(
                  "Bliv en del af et fællesskab, der omsætter solidaritet til reel hjælp.",
                  "Join a community that transforms solidarity into real support.",
                  "Forma parte de una comunidad que transforma la solidaridad en ayuda real."
                )}
              </p>
            </div>
            <a
              href="mailto:danamarksolidaritet@gmail.com?subject=Collaboration with DANAmark Solidaritet"
              data-testid="collab-empty-cta"
              className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-7 py-3.5 text-sm tracking-wide transition-colors whitespace-nowrap"
            >
              {t("Bliv samarbejdspartner", "Become a collaborator", "Conviértete en colaborador")} →
            </a>
          </div>
        )}
      </section>
    </div>
  );
}

const TIER_LABEL = {
  main:      { da: "Hovedsamarbejdspartner", en: "Main collaborator", es: "Colaborador principal" },
  supporter: { da: "Støtte",                 en: "Supporter",         es: "Colaborador" },
  friend:    { da: "Ven",                    en: "Friend",            es: "Amigo" },
};

function CollaboratorCard({ item, lang, t }) {
  const d = item.data || {};
  const slug = d.slug || item.id;
  const desc = pickLocale(d.description, lang);
  const contribution = pickLocale(d.contribution, lang);
  const logoAlt = pickLocale(d.alt, lang) || d.name || "";
  const mainAlt = pickLocale(d.mainAlt, lang) || d.name || "";
  const hasPhoto = Boolean(d.mainPhoto);
  const tier = d.tier || "supporter";
  const tierLabel = pickLocale(TIER_LABEL[tier] || TIER_LABEL.supporter, lang);

  return (
    <article
      id={slug}
      data-testid={`collab-card-${slug}`}
      className="group bg-white rounded-2xl shadow-[0_1px_2px_rgba(20,20,20,0.04),0_8px_24px_-12px_rgba(20,20,20,0.10)] hover:shadow-[0_4px_10px_rgba(20,20,20,0.06),0_18px_40px_-14px_rgba(20,20,20,0.16)] transition-all duration-500 flex flex-col scroll-mt-24 overflow-hidden ring-1 ring-ink/[0.04]"
    >
      {hasPhoto && (
        <div className="aspect-[4/3] overflow-hidden bg-ink/[0.03]">
          <img
            src={d.mainPhoto}
            alt={mainAlt}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
          />
        </div>
      )}

      <div className={`flex flex-col gap-6 flex-1 ${hasPhoto ? "p-8 md:p-10" : "p-10 md:p-12"}`}>
        {d.logo && (
          <div className={`flex items-center ${hasPhoto ? "h-16" : "h-24 md:h-28 justify-center"}`}>
            <img
              src={d.logo}
              alt={logoAlt}
              loading="lazy"
              className={`w-auto object-contain ${hasPhoto ? "max-h-14" : "max-h-24 md:max-h-28"}`}
            />
          </div>
        )}

        <div className={hasPhoto ? "" : "text-center"}>
          <h3 className="font-editorial text-2xl md:text-3xl leading-[1.15] tracking-tight">{d.name || ""}</h3>
          <div className={`mt-3 flex flex-wrap items-center gap-2 ${hasPhoto ? "" : "justify-center"}`}>
            {contribution && <div className="label-mono text-terracotta">{contribution}</div>}
            {contribution && tier && <span className="text-ink/25">·</span>}
            {tier && (
              <span data-testid={`collab-card-tier-${slug}`} className="label-mono text-ink/55">
                {tierLabel}
              </span>
            )}
          </div>
        </div>

        {desc && (
          <div className={`text-ink/75 text-base md:text-lg leading-relaxed ${hasPhoto ? "" : "text-center"}`}>
            <RichText text={desc} gapClassName="mt-4" />
          </div>
        )}

        {(d.website || d.instagram) && (
          <div className={`mt-auto pt-4 flex flex-wrap gap-3 ${hasPhoto ? "" : "justify-center"}`}>
            {d.website && (
              <a
                href={d.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`collab-card-website-${slug}`}
                className="inline-flex items-center gap-2 bg-ink text-white hover:bg-terracotta px-6 py-3 text-sm tracking-wide rounded-full transition-colors"
              >
                {t("Besøg hjemmeside", "Visit website", "Visitar web")} →
              </a>
            )}
            {d.instagram && (
              <a
                href={d.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`collab-card-instagram-${slug}`}
                className="inline-flex items-center gap-2 border border-line text-ink/70 hover:text-terracotta hover:border-terracotta px-6 py-3 text-sm tracking-wide rounded-full transition-colors"
              >
                Instagram →
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export function Legal() {
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;
  const { pathname } = useLocation();
  const doc = pathname.split("/legal/")[1]?.replace(/\/+$/, "") || "privacy";

  // Raffle terms — shown only while raffle sales are on in the CMS.
  if (doc === "raffle-terms") {
    return <RaffleGate><RaffleTerms /></RaffleGate>;
  }

  // Everything else is served from the CMS `legal_page` collection with a
  // markdown-lite renderer. Transparency now lives there too (previously a
  // dedicated component). No hardcoded copy is left in the frontend.
  return <CmsLegalPage key={doc} slug={doc} />;
}

function CmsLegalPage({ slug }) {
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || en;
  const [state, setState] = React.useState({ loading: true, page: null, error: null });
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/legal/${slug}`)
      .then(r => { if (!cancelled) setState({ loading: false, page: r.data, error: null }); })
      .catch(e => { if (!cancelled) setState({ loading: false, page: null, error: e?.response?.status || 500 }); });
    return () => { cancelled = true; };
  }, [slug]);

  usePageSEO(state.page?.title || {}, {}, { cmsSeo: state.page?.seo || (state.page ? { title: state.page.title } : null) });

  if (state.loading) {
    return <div className="container-x max-editorial py-24 max-w-3xl text-ink/60" data-testid={`legal-loading-${slug}`}>…</div>;
  }
  if (state.error || !state.page) {
    return (
      <div className="container-x max-editorial py-24 max-w-3xl" data-testid={`legal-notfound-${slug}`}>
        <div className="label-mono text-terracotta">Legal</div>
        <h1 className="font-editorial text-4xl mt-3">{slug}</h1>
        <p className="mt-6 text-ink/70">{t("Denne side er ikke tilgængelig.", "This page is not available.", "Esta página no está disponible.")}</p>
      </div>
    );
  }
  const title = pickTx(state.page.title, lang) || slug;
  const body = pickTx(state.page.body, lang);
  const updated = state.page.updated || "";
  return (
    <div className="container-x max-editorial py-24 max-w-3xl" data-testid={`legal-page-${slug}`}>
      <div className="label-mono text-terracotta">Legal</div>
      <h1 className="font-editorial text-4xl md:text-6xl mt-3 tracking-tighter" data-testid={`legal-title-${slug}`}>{title}</h1>
      {updated && (
        <div className="mt-3 label-mono text-ink/50 text-[10px]" data-testid={`legal-updated-${slug}`}>
          {t("Sidst opdateret", "Last updated", "Última actualización")}: {updated}
        </div>
      )}
      <article className="mt-8 text-ink/80 text-lg leading-relaxed space-y-5" data-testid={`legal-body-${slug}`}>
        {paragraphs(body).map((p, i) => (
          <p key={i} className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(p) }} />
        ))}
      </article>
      <p className="mt-12 text-ink/60 text-sm">Contact: danamarksolidaritet@gmail.com · CVR 45188981</p>
    </div>
  );
}

// Bold + light Markdown-ish rendering — supports **bold** only.
// Safe subset: escapes any HTML in the source, then wraps ** ** as <strong>.
function renderInlineMarkdown(text) {
  const esc = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return esc.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

function TransparencyPage() {
  // Legacy component — kept as a safety fallback so any stale import path
  // never breaks the build. The public /legal/transparency route now serves
  // CmsLegalPage("transparency") from the Legal export above.
  return <CmsLegalPage slug="transparency" />;
}

function RaffleTerms() {
  const { lang } = useI18n();
  const t = (da, en, es) => ({ da, en, es })[lang] || da;

  const bundleRows = [
    { q: 1, dkk: 50 }, { q: 2, dkk: 100 }, { q: 3, dkk: 150 },
    { q: 5, dkk: 225 }, { q: 10, dkk: 400 },
  ];

  return (
    <div className="container-x max-editorial py-16 md:py-24 max-w-3xl" data-testid="raffle-terms-page">
      <div className="label-mono text-terracotta">Legal</div>
      <h1 className="font-editorial text-4xl md:text-6xl mt-3 tracking-tighter">
        {t("Lodtrækningsvilkår", "Raffle terms", "Términos de la rifa")}
      </h1>

      <p className="mt-8 text-ink/80 leading-relaxed">
        {t(
          "Disse vilkår gælder for lodtrækningen, der arrangeres af DANAmark Solidaritet (CVR 45188981) til fordel for de områder i Spanien, der er ramt af skovbrandene.",
          "These terms apply to the raffle organised by DANAmark Solidaritet (CVR 45188981) to support the areas of Spain affected by the wildfires.",
          "Estos términos se aplican a la rifa organizada por DANAmark Solidaritet (CVR 45188981) en apoyo a las zonas de España afectadas por los incendios."
        )}
      </p>

      <h2 className="font-editorial text-2xl mt-10">
        {t("Priser og pakker", "Prices and bundles", "Precios y packs")}
      </h2>
      <table className="mt-4 w-full text-sm border border-line rounded-md overflow-hidden">
        <thead className="text-ink/60 label-mono text-[10px] text-left bg-line/10">
          <tr>
            <th className="p-3">{t("Antal lodder", "Tickets", "Boletos")}</th>
            <th className="p-3 text-right">{t("Pris", "Price", "Precio")}</th>
          </tr>
        </thead>
        <tbody>
          {bundleRows.map(r => (
            <tr key={r.q} className="border-t border-line/60">
              <td className="p-3">{r.q}</td>
              <td className="p-3 text-right font-editorial">{r.dkk} DKK</td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="mt-8 space-y-3 text-ink/80 leading-relaxed list-disc pl-6">
        <li>{t(
          "Hvert lod giver ét unikt lodnummer. En større pakke reducerer ikke antallet af deltagelser: 5 lodder = 5 numre; 10 lodder = 10 numre.",
          "Every ticket receives one unique raffle number. Buying a larger bundle does not reduce the number of entries: 5 tickets = 5 numbers; 10 tickets = 10 numbers.",
          "Cada boleto recibe un número único de rifa. Comprar un pack más grande no reduce las participaciones: 5 boletos = 5 números; 10 boletos = 10 números."
        )}</li>
        <li>{t(
          "Der udstedes i alt 300 lodder (nummereret 500–799). Salget standser, når alle lodder er solgt.",
          "A total of 300 tickets is issued (numbered 500–799). Sales stop once every ticket is sold.",
          "Se emiten 300 boletos en total (numerados 500–799). Las ventas se detienen cuando se agotan."
        )}</li>
        <li>{t(
          "Live-lodtrækningen finder sted under Fællesskabsmødet den 25. oktober 2026 kl. 17.00 (dansk tid). Praktiske detaljer deles privat med bekræftede deltagere. Sociale medier og e-mail meddeler vinderne kort efter trækningen.",
          "The live raffle draw takes place during the Community Meeting on 25 October 2026 at 17:00 (Copenhagen time). Practical details are shared privately with confirmed attendees. Winners are announced by email and on social media shortly after the draw.",
          "El sorteo en directo se celebrará durante el Encuentro comunitario el 25 de octubre de 2026 a las 17:00 (hora de Copenhague). Los detalles prácticos se comparten en privado con asistentes confirmados. Los ganadores serán anunciados por email y en redes sociales poco después del sorteo."
        )}</li>
        <li>{t(
          "Betaling sker via Stripe. Efter betalingen modtager du en bekræftelses-e-mail med dine unikke lodnumre.",
          "Payment is processed by Stripe. After payment you receive a confirmation email containing your unique ticket numbers.",
          "El pago se realiza a través de Stripe. Tras el pago recibirás un email de confirmación con tus números únicos."
        )}</li>
        <li>{t(
          "Køb, der er gennemført før denne opdatering af vilkårene, forbliver gyldige og uændrede. Tildelte lodnumre bliver ikke ændret.",
          "Purchases completed before this update of the terms remain valid and unchanged. Assigned ticket numbers are not modified.",
          "Las compras realizadas antes de esta actualización de los términos siguen siendo válidas y no se modifican. Los números asignados no se cambian."
        )}</li>
        <li>{t(
          "Refunderede køb: hvis en betaling refunderes, annulleres de tilhørende lodnumre og indgår ikke i trækningen.",
          "Refunded purchases: if a payment is refunded, the associated ticket numbers are voided and not entered into the draw.",
          "Compras reembolsadas: si un pago se reembolsa, los boletos asociados se anulan y no entran en el sorteo."
        )}</li>
        <li>{t(
          "Hele overskuddet går ubeskåret til DANAmark Solidaritets indsats for RENACE Ávila og de øvrige berørte områder.",
          "All proceeds go directly to DANAmark Solidaritet's work supporting RENACE Ávila and other affected areas.",
          "Toda la recaudación se destina directamente al trabajo de DANAmark Solidaritet en apoyo a RENACE Ávila y otras zonas afectadas."
        )}</li>
        <li>{t(
          "Personoplysninger behandles i overensstemmelse med vores privatlivspolitik og udelukkende for at gennemføre lodtrækningen og kontakte vinderne.",
          "Personal data is processed in accordance with our privacy policy and used solely to run the raffle and contact winners.",
          "Los datos personales se procesan conforme a nuestra política de privacidad y se utilizan únicamente para gestionar la rifa y contactar a los ganadores."
        )}</li>
      </ul>

      <p className="mt-10 text-ink/70">
        {t("Kontakt", "Contact", "Contacto")}: danamarksolidaritet@gmail.com · CVR 45188981
      </p>
    </div>
  );
}
